import React, { useState, useEffect } from 'react';
import { X, Upload, FileText, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface AddContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Lead {
  id: string;
  first_name: string;
  last_name: string;
  emails: string[];
  contact_role?: string;
  student_info?: {
    first_name?: string;
    last_name?: string;
  };
  guardian_info?: {
    name?: string;
    relationship?: string;
  };
  branch_id: string;
  student_id: string;
  is_registered?: boolean;
  branches: {
    name: string;
  };
}

interface Proposal {
  id: string;
  title: string;
  total_amount: number;
  currency: string;
}

const AddContractModal: React.FC<AddContractModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  const [formData, setFormData] = useState({
    lead_id: '',
    student_id: '',
    proposal_id: '',
    contract_number: '',
    contract_date: new Date().toISOString().split('T')[0],
    total_amount: '',
    currency: 'EUR',
    status: 'active',
    notes: '',
    manual_entry: false,
    manual_first_name: '',
    manual_last_name: '',
    manual_email: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchLeads();
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.lead_id) {
      const selectedLead = leads.find(l => l.id === formData.lead_id);
      if (selectedLead) {
        setFormData(prev => ({ ...prev, student_id: selectedLead.student_id || '' }));
        fetchProposals(formData.lead_id);
      }
    } else {
      setProposals([]);
    }
  }, [formData.lead_id]);

  const fetchLeads = async () => {
    try {
      console.log('Fetching leads...');
      const { data, error } = await supabase
        .from('leads')
        .select(`
          id,
          first_name,
          last_name,
          emails,
          contact_role,
          student_info,
          guardian_info,
          branch_id,
          student_id,
          is_registered,
          branches (name)
        `)
        .or('status.eq.enrolled,is_registered.eq.true')
        .order('first_name', { ascending: true });

      console.log('Leads response:', { data, error, count: data?.length });

      if (error) {
        console.error('Supabase error fetching leads:', error);
        throw error;
      }

      setLeads(data || []);
    } catch (err) {
      console.error('Error fetching leads:', err);
    }
  };

  const fetchProposals = async (leadId: string) => {
    try {
      // Query proposals table directly to avoid embedding issues
      const { data: proposalsData, error } = await supabase
        .from('proposals')
        .select(`
          id,
          title,
          total_amount,
          currency
        `)
        .eq('lead_id', leadId);

      if (error) throw error;

      if (proposalsData) {
        setProposals(proposalsData);

        if (proposalsData.length > 0) {
          setFormData(prev => ({
            ...prev,
            proposal_id: proposalsData[0].id,
            total_amount: proposalsData[0].total_amount?.toString() || '',
            currency: proposalsData[0].currency || 'EUR'
          }));
        }
      }
    } catch (err) {
      console.error('Error fetching proposals:', err);
      setProposals([]);
    }
  };

  const handleProposalChange = (proposalId: string) => {
    const selectedProposal = proposals.find(p => p.id === proposalId);
    if (selectedProposal) {
      setFormData(prev => ({
        ...prev,
        proposal_id: proposalId,
        total_amount: selectedProposal.total_amount?.toString() || '',
        currency: selectedProposal.currency || 'EUR'
      }));
    }
  };

  const generateContractNumber = () => {
    const year = new Date().getFullYear();
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `SZL-${year}-${random}`;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('Dosya boyutu 10MB\'dan küçük olmalıdır');
        return;
      }
      setSelectedFile(file);
    }
  };

  const uploadFile = async (contractId: string): Promise<string | null> => {
    if (!selectedFile) return null;

    setUploadingFile(true);
    try {
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${contractId}-${Date.now()}.${fileExt}`;
      const filePath = `contracts/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, selectedFile);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (err) {
      console.error('Error uploading file:', err);
      alert('Dosya yüklenirken hata oluştu');
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.contract_number) {
      alert('Lütfen sözleşme numarası girin');
      return;
    }

    if (!formData.total_amount || parseFloat(formData.total_amount) <= 0) {
      alert('Lütfen geçerli bir tutar girin');
      return;
    }

    let studentId = '';
    // Fix: Access branchId property correctly (camelCase)
    let branchId = user?.branchId;

    if (!user) {
      alert("Oturum bilgisi bulunamadı.");
      return;
    }

    if (formData.manual_entry) {
      if (!formData.manual_first_name || !formData.manual_last_name) {
        alert('Lütfen ad ve soyad girin');
        return;
      }

      const { data: newStudent, error: studentError } = await supabase
        .from('students')
        .insert([{
          first_name: formData.manual_first_name,
          last_name: formData.manual_last_name,
          email: formData.manual_email || null,
          branch_id: branchId
        }])
        .select()
        .single();

      if (studentError) {
        alert('Öğrenci oluşturulurken hata oluştu: ' + studentError.message);
        return;
      }

      studentId = newStudent.id;
    } else {
      if (!formData.lead_id) {
        alert('Lütfen bir öğrenci/veli seçin veya manuel giriş yapın');
        return;
      }

      const selectedLead = leads.find(l => l.id === formData.lead_id);
      if (!selectedLead) {
        alert('Seçilen lead bulunamadı');
        return;
      }

      branchId = selectedLead.branch_id;
      studentId = selectedLead.student_id;

      if (!studentId) {
        const primaryEmail = selectedLead.emails && selectedLead.emails.length > 0
          ? selectedLead.emails[0]
          : '';

        const { data: newStudent, error: studentError } = await supabase
          .from('students')
          .insert([{
            first_name: selectedLead.first_name,
            last_name: selectedLead.last_name,
            email: primaryEmail,
            branch_id: selectedLead.branch_id
          }])
          .select()
          .single();

        if (studentError) {
          alert('Öğrenci oluşturulurken hata oluştu');
          return;
        }

        studentId = newStudent.id;

        await supabase
          .from('leads')
          .update({ student_id: studentId })
          .eq('id', formData.lead_id);
      }
    }

    setLoading(true);
    try {
      const contractData: any = {
        student_id: studentId,
        proposal_id: formData.proposal_id || null,
        branch_id: branchId,
        contract_number: formData.contract_number,
        contract_date: formData.contract_date,
        total_amount: parseFloat(formData.total_amount),
        currency: formData.currency,
        status: formData.status,
        notes: formData.notes || null,
        created_by: user.id
      };

      const { data: contract, error } = await supabase
        .from('contracts')
        .insert([contractData])
        .select()
        .single();

      if (error) throw error;

      if (selectedFile && contract) {
        const fileUrl = await uploadFile(contract.id);
        if (fileUrl) {
          await supabase
            .from('contracts')
            .update({ contract_file_url: fileUrl })
            .eq('id', contract.id);
        }
      }

      alert('Sözleşme başarıyla oluşturuldu');
      onSuccess();
      handleClose();
    } catch (err: any) {
      console.error('Error creating contract:', err);
      alert(err.message || 'Sözleşme oluşturulurken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      lead_id: '',
      student_id: '',
      proposal_id: '',
      contract_number: '',
      contract_date: new Date().toISOString().split('T')[0],
      total_amount: '',
      currency: 'EUR',
      status: 'active',
      notes: '',
      manual_entry: false,
      manual_first_name: '',
      manual_last_name: '',
      manual_email: ''
    });
    setSelectedFile(null);
    setProposals([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 p-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Yeni Sözleşme Oluştur</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!formData.manual_entry}
                    onChange={() => setFormData({ ...formData, manual_entry: false })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Kayıtlı Öğrenci/Veli Seç
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={formData.manual_entry}
                    onChange={() => setFormData({ ...formData, manual_entry: true, lead_id: '' })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Manuel Giriş
                  </span>
                </label>
              </div>
            </div>

            {!formData.manual_entry ? (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Öğrenci/Veli Seçin *
                </label>
                <select
                  value={formData.lead_id}
                  onChange={(e) => setFormData({ ...formData, lead_id: e.target.value })}
                  required={!formData.manual_entry}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Öğrenci/Veli seçin...</option>
                  {leads.map(lead => {
                    const primaryEmail = lead.emails && lead.emails.length > 0 ? lead.emails[0] : '';
                    const isGuardian = lead.contact_role === 'guardian' || Boolean(lead.guardian_info?.name && !lead.student_info?.first_name);
                    const guardianName = lead.guardian_info?.name || `${lead.first_name} ${lead.last_name || ''}`.trim();

                    // Resolve student name
                    let studentName = '';
                    if (isGuardian) {
                      if (lead.student_info?.first_name) {
                        studentName = `${lead.student_info.first_name} ${lead.student_info.last_name || ''}`.trim();
                      }
                    } else {
                      studentName = `${lead.first_name} ${lead.last_name || ''}`.trim();
                    }

                    // Display Logic: Only show student name, put Guardian in parenthesis.
                    const displayName = studentName || guardianName;
                    const displayGuardianSuffix = (isGuardian && studentName && guardianName !== studentName) 
                        ? ` (Veli: ${guardianName})` 
                        : '';

                    return (
                      <option key={lead.id} value={lead.id}>
                        {displayName}
                        {displayGuardianSuffix}
                        {lead.branches ? ` - ${lead.branches.name}` : ''}
                        {primaryEmail ? ` - ${primaryEmail}` : ''}
                      </option>
                    );
                  })}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Tüm şubelerdeki öğrenci ve veliler listelenmektedir
                </p>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ad *
                  </label>
                  <input
                    type="text"
                    value={formData.manual_first_name}
                    onChange={(e) => setFormData({ ...formData, manual_first_name: e.target.value })}
                    required={formData.manual_entry}
                    placeholder="Ad"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Soyad *
                  </label>
                  <input
                    type="text"
                    value={formData.manual_last_name}
                    onChange={(e) => setFormData({ ...formData, manual_last_name: e.target.value })}
                    required={formData.manual_entry}
                    placeholder="Soyad"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    E-posta (Opsiyonel)
                  </label>
                  <input
                    type="email"
                    value={formData.manual_email}
                    onChange={(e) => setFormData({ ...formData, manual_email: e.target.value })}
                    placeholder="ornek@email.com"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            )}

            {proposals.length > 0 && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Teklif (Opsiyonel)
                </label>
                <select
                  value={formData.proposal_id}
                  onChange={(e) => handleProposalChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Teklif seçin...</option>
                  {proposals.map(proposal => (
                    <option key={proposal.id} value={proposal.id}>
                      {proposal.title} - {proposal.currency} {proposal.total_amount?.toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sözleşme Numarası *
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.contract_number}
                  onChange={(e) => setFormData({ ...formData, contract_number: e.target.value })}
                  required
                  placeholder="SZL-2024-0001"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, contract_number: generateContractNumber() })}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 text-sm font-medium"
                >
                  Oto
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sözleşme Tarihi *
              </label>
              <input
                type="date"
                value={formData.contract_date}
                onChange={(e) => setFormData({ ...formData, contract_date: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Toplam Tutar *
              </label>
              <input
                type="number"
                value={formData.total_amount}
                onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                required
                min="0"
                step="0.01"
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Para Birimi *
              </label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="TRY">TRY (₺)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Durum *
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Aktif</option>
                <option value="pending">Beklemede</option>
                <option value="completed">Tamamlandı</option>
                <option value="cancelled">İptal</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sözleşme Dosyası (Opsiyonel)
              </label>
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center">
                {selectedFile ? (
                  <div className="space-y-2">
                    <FileText className="mx-auto h-12 w-12 text-green-500" />
                    <p className="text-sm text-gray-900 dark:text-white font-medium">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <button
                      type="button"
                      onClick={() => setSelectedFile(null)}
                      className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                    >
                      Dosyayı Kaldır
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                    <label className="cursor-pointer">
                      <span className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
                        Dosya seçin
                      </span>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      PDF, DOC veya DOCX (Maks. 10MB)
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notlar (Opsiyonel)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                placeholder="Sözleşme ile ilgili notlar..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
            <button
              type="submit"
              disabled={loading || uploadingFile}
              className="flex-1 bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading || uploadingFile ? 'Oluşturuluyor...' : 'Sözleşme Oluştur'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              disabled={loading || uploadingFile}
              className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-6 py-2.5 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              İptal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddContractModal;
